import React, { useEffect, useReducer, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Upload, Trash2 } from "lucide-react";
import { adminAPI, absUrl } from "../../services/api";
import { CATEGORIES } from "../../lib/categories";
import RichTextEditor from "../../components/RichTextEditor";
import Seo from "../../components/Seo";
import "./Admin.css";

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  featured_image: "",
  author: "Race377 Editorial",
  category: "formula-1",
  tags: [],
  status: "draft",
  meta_title: "",
  meta_description: "",
};

export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [data, setData] = useState(empty);
  const [tagsInput, setTagsInput] = useState("");
  const [loadState, dispatchLoad] = useReducer(
    (s, a) => ({ ...s, ...a }),
    { loading: !isNew, error: "" }
  );
  const { loading, error } = loadState;
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const setError = (e) => dispatchLoad({ error: e });

  useEffect(() => {
    if (isNew) return;
    dispatchLoad({ loading: true, error: "" });
    adminAPI
      .get(id)
      .then((r) => {
        setData(r.data);
        setTagsInput((r.data.tags || []).join(", "));
        dispatchLoad({ loading: false });
      })
      .catch(() => dispatchLoad({ loading: false, error: "Article not found" }));
  }, [id, isNew]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const r = await adminAPI.upload(file);
      set("featured_image", r.data.url);
    } catch (err) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (overrideStatus) => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...data,
        status: overrideStatus || data.status,
        tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (!payload.title.trim()) throw new Error("Title is required");
      if (!payload.excerpt.trim()) throw new Error("Excerpt is required");
      if (!payload.body || payload.body === "<p></p>") throw new Error("Article body is required");

      if (isNew) {
        const r = await adminAPI.create(payload);
        navigate(`/admin/articles/${r.data.id}`, { replace: true });
      } else {
        const r = await adminAPI.update(id, payload);
        setData(r.data);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Save failed";
      setError(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-shell"><div className="loading-container"><div className="loading-spinner" /></div></div>;
  }

  return (
    <div className="admin-shell" data-testid="admin-article-editor">
      <Seo title={isNew ? "New article" : "Edit article"} noIndex />
      <header className="admin-header">
        <div className="admin-header__inner">
          <Link to="/admin" className="admin-link-btn" data-testid="admin-back-dashboard">
            <ArrowLeft size={16} /> Back to dashboard
          </Link>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="btn-secondary"
              onClick={() => save("draft")}
              disabled={saving}
              data-testid="admin-save-draft"
            >
              <Save size={16} style={{ marginRight: 6 }} />
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              className="btn-primary"
              onClick={() => save("published")}
              disabled={saving}
              data-testid="admin-publish"
            >
              {data.status === "published" ? "Update" : "Publish"}
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main admin-editor">
        {error && <div className="admin-error" data-testid="admin-editor-error">{error}</div>}

        <div className="admin-editor__grid">
          <div className="admin-editor__main">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={data.title}
                onChange={(e) => set("title", e.target.value)}
                className="admin-title-input"
                placeholder="Article headline"
                data-testid="admin-title-input"
              />
            </div>
            <div className="form-group">
              <label>Excerpt</label>
              <textarea
                value={data.excerpt}
                onChange={(e) => set("excerpt", e.target.value)}
                rows={2}
                placeholder="One or two sentences that summarise the story."
                data-testid="admin-excerpt-input"
              />
            </div>
            <div className="form-group">
              <label>Body</label>
              <RichTextEditor value={data.body} onChange={(html) => set("body", html)} />
            </div>
          </div>

          <aside className="admin-editor__sidebar">
            <div className="admin-card">
              <h3 className="caption">Status</h3>
              <p style={{ marginTop: 6, fontWeight: 700, textTransform: "uppercase" }}>{data.status}</p>
              {data.published_at && (
                <p className="caption" style={{ marginTop: 6 }}>
                  Published: {new Date(data.published_at).toLocaleString()}
                </p>
              )}
            </div>

            <div className="admin-card">
              <h3 className="caption">Featured image</h3>
              {data.featured_image ? (
                <div className="admin-img-preview">
                  <img src={absUrl(data.featured_image)} alt="" />
                  <button
                    className="admin-icon-btn admin-icon-btn--danger"
                    onClick={() => set("featured_image", "")}
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : (
                <p className="caption" style={{ color: "var(--text-secondary)", marginTop: 6 }}>No image set</p>
              )}
              <label className="admin-upload-btn">
                <Upload size={14} />
                {uploading ? "Uploading…" : (data.featured_image ? "Replace image" : "Upload image")}
                <input type="file" accept="image/*" onChange={onUpload} style={{ display: "none" }} data-testid="admin-image-upload" />
              </label>
              <input
                type="text"
                value={data.featured_image || ""}
                onChange={(e) => set("featured_image", e.target.value)}
                placeholder="Or paste image URL"
                style={{ marginTop: 8 }}
                data-testid="admin-image-url"
              />
            </div>

            <div className="admin-card">
              <h3 className="caption">Category</h3>
              <select
                value={data.category}
                onChange={(e) => set("category", e.target.value)}
                data-testid="admin-category-select"
              >
                {CATEGORIES.map((c) => (
                  <option value={c.slug} key={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="admin-card">
              <h3 className="caption">Author</h3>
              <input
                type="text"
                value={data.author}
                onChange={(e) => set("author", e.target.value)}
                data-testid="admin-author"
              />
            </div>

            <div className="admin-card">
              <h3 className="caption">Tags (comma separated)</h3>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="verstappen, monaco, qualifying"
                data-testid="admin-tags"
              />
            </div>

            <div className="admin-card">
              <h3 className="caption">Slug</h3>
              <input
                type="text"
                value={data.slug || ""}
                onChange={(e) => set("slug", e.target.value)}
                placeholder="auto-generated from title"
                data-testid="admin-slug"
              />
            </div>

            <div className="admin-card">
              <h3 className="caption">SEO — Meta title</h3>
              <input
                type="text"
                value={data.meta_title || ""}
                onChange={(e) => set("meta_title", e.target.value)}
                placeholder="Defaults to title"
                data-testid="admin-meta-title"
              />
              <h3 className="caption" style={{ marginTop: 12 }}>SEO — Meta description</h3>
              <textarea
                value={data.meta_description || ""}
                onChange={(e) => set("meta_description", e.target.value)}
                rows={3}
                placeholder="Defaults to excerpt (160 char max recommended)"
                data-testid="admin-meta-description"
              />
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
